import React, { useState } from 'react';
import { galleryImages } from '../data/mock';
import { X } from 'lucide-react';

const Gallery = () => {
  const [selectedImage, setSelectedImage] = useState(null);
  const [activeFilter, setActiveFilter] = useState('all');

  const categories = [
    { value: 'all', label: 'Tous' },
    { value: 'bois', label: 'Bois' },
    { value: 'pvc', label: 'PVC' },
    { value: 'aluminium', label: 'Aluminium' },
    { value: 'serrurerie', label: 'Serrurerie' }
  ];

  const filteredImages = activeFilter === 'all'
    ? galleryImages
    : galleryImages.filter(img => img.category === activeFilter);

  return (
    <section id="realisations" className="py-24 bg-[#EEF1F5]">
      <div className="max-w-7xl mx-auto px-4">
        {/* Section Header */}
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-[#01162D] mb-4 tracking-tight">
            Nos <span className="text-[#1F6FD6]">Réalisations</span>
          </h2>
          <div className="w-20 h-1 bg-[#1F6FD6] mx-auto mb-6"></div>
          <p className="text-lg text-[#647A97] max-w-3xl mx-auto">
            Découvrez quelques-unes de nos réalisations récentes
          </p>
        </div>

        {/* Filter Buttons */}
        <div className="flex flex-wrap justify-center gap-2 md:gap-3 mb-8 md:mb-12">
          {categories.map((category) => (
            <button
              key={category.value}
              onClick={() => setActiveFilter(category.value)}
              className={`px-4 md:px-6 py-2.5 rounded-md font-medium text-sm md:text-base transition-all duration-300 ${
                activeFilter === category.value
                  ? 'bg-[#1F6FD6] text-white shadow-lg hover:bg-[#01162D]'
                  : 'bg-white text-[#01162D] hover:bg-[#E3EDF9] hover:text-[#1F6FD6] border-2 border-[#C9D6E6] hover:border-[#1F6FD6]'
              }`}
              data-testid={`filter-${category.value}`}
            >
              {category.label}
            </button>
          ))}
        </div>

        {filteredImages.length === 0 && (
          <div className="text-center py-16 bg-white rounded-md border border-[#C9D6E6]" data-testid="gallery-empty">
            <p className="text-[#01162D] font-semibold text-lg mb-2">Photos à venir pour cette catégorie</p>
            <p className="text-[#647A97] text-sm">Contactez-nous pour découvrir nos réalisations en {categories.find(c => c.value === activeFilter)?.label}.</p>
          </div>
        )}

        {/* Gallery Grid */}
        <div className="grid grid-cols-2 lg:grid-cols-3 gap-3 md:gap-6">
          {filteredImages.map((image, index) => (
            <div
              key={image.id}
              className={`group relative overflow-hidden rounded-md cursor-pointer bg-[#E3EDF9] ${image.wide ? "col-span-2 aspect-[3/2]" : "aspect-[3/4]"}`}
              onClick={() => setSelectedImage(image)}
              data-testid={`gallery-item-${image.id}`}
              style={{ animationDelay: `${index * 50}ms` }}
            >
              <img
                src={image.url}
                alt={image.title}
                loading="lazy"
                className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#01162D]/90 via-[#01162D]/40 to-transparent opacity-100 md:opacity-0 md:group-hover:opacity-100 transition-opacity duration-300 flex items-end p-3 md:p-6">
                <div className="text-white">
                  <h3 className="text-xs md:text-lg font-semibold mb-0.5 md:mb-1 leading-snug line-clamp-2">{image.title}</h3>
                  <p className="text-[11px] md:text-sm text-[#3B8BEF] capitalize font-medium">{image.category}</p>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Lightbox Modal */}
        {selectedImage && (
          <div
            className="fixed inset-0 bg-black/95 z-50 flex items-center justify-center p-4"
            onClick={() => setSelectedImage(null)}
            data-testid="gallery-lightbox"
          >
            <button
              className="absolute top-3 right-3 md:top-4 md:right-4 z-10 text-white transition-colors bg-[#1F6FD6] p-3 rounded-full hover:bg-[#3B8BEF]" aria-label="Fermer"
              onClick={() => setSelectedImage(null)}
              data-testid="gallery-lightbox-close"
            >
              <X size={32} />
            </button>
            <div className="max-w-5xl w-full" onClick={(e) => e.stopPropagation()}>
              <img
                src={selectedImage.url}
                alt={selectedImage.title}
                className="w-full h-auto max-h-[75vh] object-contain rounded-md"
              />
              <div className="text-center mt-4">
                <h3 className="text-xl font-semibold text-white mb-2">
                  {selectedImage.title}
                </h3>
                <p className="text-[#1F6FD6] capitalize font-medium">{selectedImage.category}</p>
              </div>
            </div>
          </div>
        )}
      </div>
    </section>
  );
};

export default Gallery;
